/**
 * PanelDashboard — Inc-4c/4e (model-view-lovable-density-alignment).
 *
 * Rendered by <ModelGrid> when `layout.composition?.grammar === 'panel-dashboard'`.
 * Implements the Lovable "institutional brief" multi-panel composition:
 *   - Outer CSS grid: `1fr 1.4fr 1fr` (user-locked faithful Lovable ratios,
 *     remediation-plan.md §2 §5.2; each panel assigned to its `column` index).
 *   - Inc-4e: multiple panels per column. Panels are grouped by `column` index
 *     and stacked vertically within each grid track (flex-col, gap-3). Sort
 *     within each column is startRow ascending so the visual order matches the
 *     workbook row order. `column` indices > 2 are clamped to 2 (defensive —
 *     current heuristic never emits > 3 columns, but render stays correct
 *     against future ingest changes without a branch).
 *   - Each panel: bordered box (panel-border-hi chrome + panel-bg fill) with a
 *     grey opener band (bg-band-section, matching SectionHeaderRow chrome from
 *     Inc-1/3.1) + compact key-value rows.
 *   - Value coloring: reuses getCellClassName + formatCellValue + resolveValue
 *     from the same Inc-1/2/3 pipeline — zero behavior divergence between
 *     panel-dashboard and G2/G3 paths.
 *   - Cell selection (2026-05-21 user decision: "Deal Summary should function as
 *     the other tabs"): when the caller passes `onCellSelect`, ALL non-empty cells
 *     (label + value) are interactive — click / Enter / Space fires `onCellSelect`,
 *     the selected cell gets the same `outline outline-2 outline-accent
 *     outline-offset-[-2px] z-[1]` ring as DataCell, and `cursor-pointer` is
 *     added. When `onCellSelect` is absent the component falls back to the prior
 *     read-only render (no click, no ring, no tabIndex). `readOnly` is threaded
 *     for symmetry / future edit-gate parity with CellDetailPanel.
 *   - No inline edit UI — CellDetailPanel (rendered by ModelLayout) owns the
 *     edit flow; PanelDashboard only fires selection.
 *   - Generalization (plan §4): zero per-model / per-sheet branch. All panel
 *     geometry comes from composition.panels[]; all cell data from cellIndex +
 *     cellValues / inputValues. Works identically for mfv1 and op-asset.
 *
 * Architecture note: no virtualization — panels are short key-value lists
 * (DealSummary panels fit entirely in viewport, per the Lovable reference).
 * Each row is a static DOM element (not a virtual item).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type {
  CompositionPanel,
  GridCell,
  SheetLayout,
} from '@arcanum-hq/shared-types';
import { DEFAULT_ZOOM, type ZoomLevel } from '@arcanum-hq/shared-types';
import type { ModelViewSchemaSubset } from '@/lib/api';
import { buildCellIndex, resolveValue } from './grid-helpers';
import {
  formatCellValue,
  getCellClassName,
  TIER_BAND_CLASS,
} from './cell-style';

// DIMENSIONAL zoom for the panel-dashboard render path
// (model-view-excel-parity post-ship remediation, 2026-05-25). Mirrors the
// virtualized ModelGrid's dimensional-zoom contract (ADR-0012) — bases match
// ModelGrid's BASE_ROW_HEIGHT (22), BASE_FONT_SIZE (11), BASE_HEADER_FONT_SIZE
// (12). PanelDashboard skipped Phase 2 zoom plumbing because it isn't
// virtualized; user reported "zoom not working on DealSummary" 2026-05-25 —
// this is the fix.
const BASE_ROW_HEIGHT = 22;
const BASE_FONT_SIZE = 11;
const BASE_HEADER_FONT_SIZE = 12;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PanelDashboardProps {
  layout: SheetLayout;
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  schema: ModelViewSchemaSubset;
  /**
   * Currently selected cell, lifted from ModelLayout via ModelGrid.
   * When set, the matching cell renders the DataCell selection ring
   * (`outline outline-2 outline-accent outline-offset-[-2px] z-[1]`).
   * Absent → no ring rendered (read-only / public consumer).
   */
  selectedCell?: { row: number; col: number } | null;
  /**
   * Fires when a non-empty cell is clicked or activated via keyboard.
   * Absent → cells render non-interactive (back-compat for future
   * read-only / public-share consumers).
   */
  onCellSelect?: (row: number, col: number) => void;
  /**
   * Passed through for edit-gate symmetry with CellDetailPanel.
   * PanelDashboard itself has no inline edit UI — only fires selection.
   */
  readOnly?: boolean;
  /**
   * Dimensional zoom factor (50, 75, 100, 125, 150, 200). Defaults to
   * DEFAULT_ZOOM (100). Mirrors ModelGrid's zoom contract — same 6 ZoomLevels,
   * same `Math.round(BASE * (zoom / 100))` derivation. Threaded through to
   * each row's inline `style` so font + row-height + horizontal padding scale
   * together with the user's zoom-slider / Ctrl+Shift+= shortcut.
   */
  zoom?: ZoomLevel;
  /**
   * Tree-navigator / deep-link scroll target. Mirrors ModelGrid's contract:
   * the parent (<ModelLayout>) bumps a `nonce` per click so re-clicking the
   * SAME cell re-fires scroll + pulse via referential inequality. Shape +
   * semantics intentionally identical to ModelGrid's `scrollToCell` prop
   * (`ModelGrid.tsx:60`) so the parent threads a single state through both
   * render paths without branching.
   *
   * Surfaced as P1 bug by `new-development-input-roundtrip` walkthrough
   * Session 3 (2026-05-26): DealSummary cells failed the click→scroll→pulse
   * contract because this prop was never threaded through. At 100% zoom 10/23
   * panel cells were out-of-viewport after click; 15/23 at 125%. Every failure
   * had `data-pulsing="false"` confirming the cell never received the signal.
   */
  scrollToCell?: { row: number; col: number; nonce: number } | null;
}

// ---------------------------------------------------------------------------
// Panel column count — derived from the maximum `column` index in panels[].
// The CSS grid spans exactly that many equal-width columns.
// Lovable target: 3 columns with `1fr 1.4fr 1fr` ratios (user-locked decision,
// remediation-plan.md R1 §2.5 + §5.2).
// Inc-4e: column indices are clamped to [0, 2] (max 3 columns). Panels with
// column > 2 are assigned to column 2 (last track) — defensive against future
// ingest changes without introducing a branch in the rendering logic.
// ---------------------------------------------------------------------------

/**
 * Returns the CSS `grid-template-columns` value for the outer panel grid.
 * With 3 panels → `1fr 1.4fr 1fr`; 2 panels → `1fr 1fr`; else → `1fr`.
 * Clipped to 3 at most (the Lovable reference has exactly 3 columns).
 *
 * Each track carries a zoom-scaled minimum width so labels + values keep
 * their space when the user zooms in (post-ship remediation 2026-05-25 —
 * pre-fix, fixed `minmax(0, ...)` let value columns squeeze labels to
 * `L…` / `Prod…` ellipses at 150%+ zoom). The 320px base mirrors the
 * intrinsic comfortable width of the canonical 3-column DealSummary
 * layout at 100% zoom; at higher zooms the outer container's
 * `overflow-auto` takes over and the grid scrolls horizontally.
 */
function gridTemplateColumns(panelCount: number, zoomFactor: number): string {
  const baseMinPx = Math.round(320 * zoomFactor);
  const wideMinPx = Math.round(baseMinPx * 1.4);
  if (panelCount >= 3)
    return `minmax(${baseMinPx}px, 1fr) minmax(${wideMinPx}px, 1.4fr) minmax(${baseMinPx}px, 1fr)`;
  if (panelCount === 2)
    return `minmax(${baseMinPx}px, 1fr) minmax(${baseMinPx}px, 1fr)`;
  return `minmax(${baseMinPx}px, 1fr)`;
}

// ---------------------------------------------------------------------------
// Cell filtering
// ---------------------------------------------------------------------------

/**
 * Extract cells from the index that fall inside the panel's [startRow,endRow]
 * × [startCol,endCol] window. Returns cells in row-major order.
 *
 * Additive helper — does NOT change any existing helper's behavior. Uses the
 * same `cellIndex` shape produced by buildCellIndex (Map<"row-col", GridCell>).
 */
function getCellsInPanel(
  cellIndex: Map<string, GridCell>,
  panel: CompositionPanel
): GridCell[] {
  const cells: GridCell[] = [];
  for (let r = panel.startRow; r <= panel.endRow; r++) {
    for (let c = panel.startCol; c <= panel.endCol; c++) {
      const cell = cellIndex.get(`${r}-${c}`);
      if (cell && cell.cellType !== 'empty' && !cell.mergedInto) {
        cells.push(cell);
      }
    }
  }
  return cells;
}

// ---------------------------------------------------------------------------
// PanelCard — one independently bordered panel
// ---------------------------------------------------------------------------

interface PanelCardProps {
  panel: CompositionPanel;
  cells: GridCell[];
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  schema: ModelViewSchemaSubset;
  sheetName: string;
  selectedCell?: { row: number; col: number } | null;
  pulsedCell?: { row: number; col: number } | null;
  onCellSelect?: (row: number, col: number) => void;
  zoom: ZoomLevel;
}

/**
 * One bordered panel box.
 *
 * Structure (Lovable §2.1-2.3 grammar):
 *   - Outer: `border border-panel-border-hi rounded-[3px] bg-panel-bg`
 *     (matches Inc-1 brick chrome; the border "boxes" the entire panel
 *     including its opener band).
 *   - Opener band: `bg-band-section text-text-bright text-xs font-mono
 *     font-bold tracking-[0.5px] px-3 py-1 border-b border-panel-border`
 *     (matches SectionHeaderRow chrome from Inc-3.1).
 *   - Body: compact key-value rows. Label cells span full width; non-label
 *     cells render as right-aligned value with left-aligned label when a
 *     label cell immediately precedes them in the same row.
 */
function PanelCard({
  panel,
  cells,
  cellValues,
  inputValues,
  schema,
  sheetName,
  selectedCell,
  pulsedCell,
  onCellSelect,
  zoom,
}: PanelCardProps) {
  // Group cells by row for paired label/value rendering.
  const rowMap = useMemo(() => {
    const map = new Map<number, GridCell[]>();
    for (const cell of cells) {
      if (!map.has(cell.row)) map.set(cell.row, []);
      map.get(cell.row)!.push(cell);
    }
    // Sort rows ascending; sort cells within each row by col ascending.
    for (const [, rowCells] of map) {
      rowCells.sort((a, b) => a.col - b.col);
    }
    return map;
  }, [cells]);

  const sortedRows = useMemo(
    () => [...rowMap.keys()].sort((a, b) => a - b),
    [rowMap]
  );

  // DIMENSIONAL zoom scales header font; row fonts/heights are scaled
  // per-row inside <PanelRow> via the same factor.
  const zoomFactor = zoom / 100;
  const headerFontSize = Math.round(BASE_HEADER_FONT_SIZE * zoomFactor);
  return (
    <div
      data-testid={`model-panel-${panel.column}-${panel.startRow}`}
      className="border border-panel-border-hi rounded-[3px] bg-panel-bg flex flex-col min-h-0"
    >
      {/* Opener band — grey section header, matches SectionHeaderRow chrome */}
      <div
        className="flex items-center px-3 py-1 bg-band-section text-text-bright font-mono font-bold tracking-[0.5px] border-b border-panel-border rounded-t-[3px] shrink-0"
        style={{ fontSize: `${headerFontSize}px` }}
      >
        {panel.label}
      </div>

      {/* Panel body — key-value rows */}
      <div className="flex flex-col">
        {sortedRows.map((rowNum) => {
          const rowCells = rowMap.get(rowNum)!;
          return (
            <PanelRow
              key={rowNum}
              rowCells={rowCells}
              cellValues={cellValues}
              inputValues={inputValues}
              schema={schema}
              sheetName={sheetName}
              selectedCell={selectedCell}
              pulsedCell={pulsedCell}
              onCellSelect={onCellSelect}
              zoom={zoom}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PanelRow — one row inside a panel
// ---------------------------------------------------------------------------

interface PanelRowProps {
  rowCells: GridCell[];
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  schema: ModelViewSchemaSubset;
  sheetName: string;
  selectedCell?: { row: number; col: number } | null;
  pulsedCell?: { row: number; col: number } | null;
  onCellSelect?: (row: number, col: number) => void;
  zoom: ZoomLevel;
}

/**
 * Selection ring class — verbatim copy of DataCell's selected-state class
 * (cell-style.ts getCellClassName selectionRing, 2026-05-21). Kept as a
 * module-level constant so any future change to DataCell's ring is a single
 * grep target.
 */
const SELECTION_RING =
  'outline outline-2 outline-accent outline-offset-[-2px] z-[1]';

/**
 * One data row inside a panel. Renders cells in column order.
 *
 * Layout strategy (Lovable §2.5 TwoColRow: `gridTemplateColumns: "1fr auto"`):
 *   - Single label cell → full-width, left-aligned (section sub-header style).
 *   - Label + value(s) → label takes 1fr, values auto-right-aligned.
 *   - Multiple values (no label) → flex-row, all right-aligned.
 *
 * Interaction (2026-05-21 user decision — mirrors DataCell contract):
 *   - When `onCellSelect` is provided, ALL non-empty cells (label + value) are
 *     interactive: `role="button"`, `tabIndex={0}`, click + Enter/Space fire
 *     `onCellSelect(row, col)`, and `cursor-pointer` affordance is added.
 *   - The selected cell (matched via `selectedCell`) shows the same DataCell
 *     selection ring (`outline outline-2 outline-accent outline-offset-[-2px]`).
 *   - When `onCellSelect` is absent, cells render non-interactive (read-only /
 *     public-share consumer back-compat — no click, no ring, no tabIndex).
 */
function PanelRow({
  rowCells,
  cellValues,
  inputValues,
  schema,
  sheetName,
  selectedCell,
  pulsedCell,
  onCellSelect,
  zoom,
}: PanelRowProps) {
  // Separate label cells from value cells in this row.
  const labelCells = rowCells.filter((c) => c.cellType === 'label');
  const valueCells = rowCells.filter((c) => c.cellType !== 'label');
  // DIMENSIONAL zoom — single-source per render. Mirrors ModelGrid's
  // `Math.round(BASE * zoomFactor)` derivation.
  // Post-ship remediation 2026-05-25: floor row height at 16px and minimum
  // readable font at 9px so 50%/75% zooms stay readable; scale horizontal
  // padding with zoom so input cells' amber backgrounds don't visually
  // overlap (px-2 = 8px stays 8px regardless of zoom in pure Tailwind,
  // making amber boxes look squashed together when font shrinks). Per-side
  // padding bottoms out at 2px so cells stay visually distinct.
  const zoomFactor = zoom / 100;
  const rowHeight = Math.max(16, Math.round(BASE_ROW_HEIGHT * zoomFactor));
  const fontSize = Math.max(9, Math.round(BASE_FONT_SIZE * zoomFactor));
  const labelPadX = Math.max(4, Math.round(12 * zoomFactor));
  const valuePadX = Math.max(2, Math.round(8 * zoomFactor));
  const valueGap = Math.max(2, Math.round(4 * zoomFactor));
  const rowStyle = {
    height: `${rowHeight}px`,
    fontSize: `${fontSize}px`,
    lineHeight: `${rowHeight}px`,
  } as const;
  const cellTextStyle = {
    fontSize: `${fontSize}px`,
  } as const;
  const labelSlotStyle = {
    paddingLeft: `${labelPadX}px`,
    paddingRight: `${labelPadX}px`,
  } as const;
  const valueCellStyle = {
    fontSize: `${fontSize}px`,
    // lineHeight must match the row container so the amber input bg doesn't
    // bleed vertically into adjacent rows — `getCellClassName` hardcodes
    // `leading-[22px]` which fights the zoom-scaled row height at low zooms
    // (post-ship remediation 2026-05-25, Collection Loss panel overflow).
    lineHeight: `${rowHeight}px`,
    height: `${rowHeight}px`,
    paddingLeft: `${valuePadX}px`,
    paddingRight: `${valuePadX}px`,
  } as const;
  const valueRowStyle = {
    ...rowStyle,
    columnGap: `${valueGap}px`,
  } as const;

  // Full-width label row (section sub-header, no associated value in this row).
  // 2026-05-26: ONLY collapse to the sub-header layout when there's a single
  // label cell — multi-label rows (e.g. "Project Uses | $ Amount | $ / SF"
  // column headers on Capital Stack) fall through to the paired branch below,
  // where tail labels render as right-aligned column headers alongside their
  // value-column positions.
  if (labelCells.length === 1 && valueCells.length === 0) {
    const cell = labelCells[0];
    const value = resolveValue(cell, cellValues, inputValues, sheetName);
    const display = formatCellValue(value, cell, schema);
    if (!display) return null;
    const isSelected =
      selectedCell?.row === cell.row && selectedCell?.col === cell.col;
    const interactive = onCellSelect != null;
    return (
      <div
        key={`${cell.row}-${cell.col}`}
        role="gridcell"
        id={`cell-${sheetName}-${cell.row}-${cell.col}`}
        aria-rowindex={cell.row + 1}
        aria-colindex={cell.col + 1}
        aria-selected={isSelected || undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={
          interactive ? () => onCellSelect(cell.row, cell.col) : undefined
        }
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCellSelect(cell.row, cell.col);
                }
              }
            : undefined
        }
        className={clsx(
          'flex items-center font-mono text-text-muted border-b border-panel-border/30 shrink-0',
          interactive && 'cursor-pointer',
          isSelected && SELECTION_RING
        )}
        style={{ ...rowStyle, ...labelSlotStyle }}
        data-testid={`panel-row-label-${cell.row}`}
        title={display}
      >
        <span className="truncate">{display}</span>
      </div>
    );
  }

  // Paired label + value(s): label 1fr, values auto right-aligned.
  // Matches Lovable TwoColRow `gridTemplateColumns:"1fr auto"`.
  // 2026-05-26 — when the first label carries `tier` (e.g. 'description-header'
  // from the layout-extractor post-pass for panel-internal sub-section header
  // rows like "Project Uses"), apply TIER_BAND_CLASS to the row container so
  // the band paints across the full row width (matching panel-opener chrome).
  const tierBand = labelCells[0]?.tier
    ? TIER_BAND_CLASS[labelCells[0].tier]
    : '';
  return (
    <div
      className={clsx(
        'flex items-center border-b border-panel-border/30 shrink-0',
        tierBand
      )}
      style={valueRowStyle}
      data-testid={`panel-row-${rowCells[0]?.row ?? 0}`}
    >
      {/* Label slot (1fr) — interactive when onCellSelect is provided (mirrors DataCell) */}
      {labelCells.length > 0 &&
        (() => {
          const c = labelCells[0];
          const v = resolveValue(c, cellValues, inputValues, sheetName);
          const labelDisplay = formatCellValue(v, c, schema) ?? '';
          const isSelected =
            selectedCell?.row === c.row && selectedCell?.col === c.col;
          const interactive = onCellSelect != null;
          return (
            <div
              role="gridcell"
              id={`cell-${sheetName}-${c.row}-${c.col}`}
              aria-rowindex={c.row + 1}
              aria-colindex={c.col + 1}
              aria-selected={isSelected || undefined}
              tabIndex={interactive ? 0 : undefined}
              onClick={
                interactive ? () => onCellSelect(c.row, c.col) : undefined
              }
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCellSelect(c.row, c.col);
                      }
                    }
                  : undefined
              }
              className={clsx(
                'flex-1 min-w-0',
                interactive && 'cursor-pointer',
                isSelected && SELECTION_RING
              )}
              style={labelSlotStyle}
              title={labelDisplay}
            >
              <span
                className="block truncate font-mono text-text-muted"
                style={cellTextStyle}
              >
                {labelDisplay}
              </span>
            </div>
          );
        })()}

      {/* Value cell(s): auto, right-aligned, use getCellClassName for value-color */}
      {valueCells.map((cell) => {
        const value = resolveValue(cell, cellValues, inputValues, sheetName);
        const display = formatCellValue(value, cell, schema);
        const isSelected =
          selectedCell?.row === cell.row && selectedCell?.col === cell.col;
        const isPulsing =
          pulsedCell?.row === cell.row && pulsedCell?.col === cell.col;
        const interactive = onCellSelect != null;
        const className = getCellClassName(cell, isSelected, {
          isError: false,
          isMissing: false,
          bracketLeft: false,
          pulsing: isPulsing,
          sheet: sheetName,
        });
        // F-I2: empty input cells need an accessible name so screen-reader
        // users navigating row-by-row hear "<label> — no value set" instead
        // of an unnamed gridcell. Pull the row's label (if any) for context.
        const isEmptyInput =
          cell.cellType === 'input' && (display == null || display === '');
        // 2026-05-27 — discoverable placeholder for empty `dataType:'location'`
        // input cells. Default DealSummary column widths make a blank
        // single-cell location input visually disappear; a muted-italic
        // "Set address" tells the user it's clickable + what to do.
        // Generalized via the schema InputDefinition's dataType, so any
        // future location-typed input picks it up automatically.
        const isEmptyLocationInput =
          isEmptyInput &&
          cell.namedRange != null &&
          (schema.inputs as Array<{ name: string; dataType?: string }>).some(
            (i) => i.name === cell.namedRange && i.dataType === 'location'
          );
        const rowLabelText =
          labelCells[0]?.text ?? labelCells[0]?.formula ?? '';
        const ariaLabel = isEmptyInput
          ? rowLabelText
            ? `${rowLabelText} — no value set`
            : 'No value set'
          : undefined;
        // 2026-05-26 — Location and other long string values were getting
        // permanently clipped because the value cell has `shrink-0` and the
        // text was rendered inline. We:
        //   (a) add `title={display}` so the full text is visible on hover
        //   (b) for string-typed values (location addresses, etc.), allow
        //       the text to wrap inside a max-width slot so it doesn't
        //       silently truncate. Numeric/currency stays single-line for
        //       column alignment.
        // Bound long string values so they don't elbow the label out of the
        // row. Currency/percent/integer displays stay short; addresses,
        // city-state lists, and long text labels overflow if unbounded.
        const isLongString = typeof display === 'string' && display.length > 24;
        return (
          <div
            key={`${cell.row}-${cell.col}`}
            role="gridcell"
            id={`cell-${sheetName}-${cell.row}-${cell.col}`}
            aria-rowindex={cell.row + 1}
            aria-colindex={cell.col + 1}
            aria-selected={isSelected || undefined}
            aria-label={ariaLabel}
            tabIndex={interactive ? 0 : undefined}
            onClick={
              interactive ? () => onCellSelect(cell.row, cell.col) : undefined
            }
            onKeyDown={
              interactive
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onCellSelect(cell.row, cell.col);
                    }
                  }
                : undefined
            }
            data-testid={`panel-cell-${cell.row}-${cell.col}`}
            data-cell-type={cell.cellType}
            data-pulsing={isPulsing ? 'true' : undefined}
            title={typeof display === 'string' ? display : undefined}
            className={clsx(
              // Strip the absolute-positioning / sizing classes from getCellClassName
              // (those are virtualizer-specific). Apply only the color/font tokens.
              'text-right shrink-0 tabular-nums',
              isLongString && 'max-w-[60%] truncate',
              interactive && 'cursor-pointer',
              // Inherit value-kind color from getCellClassName (text-accent for
              // xref, text-blue bg-input-fill-amber for input, etc.).
              // We extract the token classes from the full className string by
              // passing getCellClassName's result as a class composition.
              className
            )}
            style={valueCellStyle}
          >
            {isEmptyLocationInput ? (
              <span className="text-text-muted italic">Set address</span>
            ) : (
              display ?? <span className="text-text-muted">—</span>
            )}
          </div>
        );
      })}

      {/* Tail label cells (column-header row inside a panel — e.g. "$ Amount",
          "$ / SF" sitting above their value columns). Rendered with the same
          right-aligned shape as value cells so they align over the columns
          they head; getCellClassName carries the workbook styling (underline,
          horizontalAlign) from the cell's style payload. */}
      {labelCells.slice(1).map((cell) => {
        const value = resolveValue(cell, cellValues, inputValues, sheetName);
        const display = formatCellValue(value, cell, schema);
        if (!display) return null;
        const isSelected =
          selectedCell?.row === cell.row && selectedCell?.col === cell.col;
        const interactive = onCellSelect != null;
        const className = getCellClassName(cell, isSelected, {
          isError: false,
          isMissing: false,
          bracketLeft: false,
          pulsing: false,
          sheet: sheetName,
        });
        return (
          <div
            key={`${cell.row}-${cell.col}`}
            role="gridcell"
            id={`cell-${sheetName}-${cell.row}-${cell.col}`}
            aria-rowindex={cell.row + 1}
            aria-colindex={cell.col + 1}
            aria-selected={isSelected || undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={
              interactive ? () => onCellSelect(cell.row, cell.col) : undefined
            }
            onKeyDown={
              interactive
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onCellSelect(cell.row, cell.col);
                    }
                  }
                : undefined
            }
            data-testid={`panel-cell-${cell.row}-${cell.col}`}
            data-cell-type={cell.cellType}
            className={clsx(
              'text-right shrink-0',
              interactive && 'cursor-pointer',
              className
            )}
            style={valueCellStyle}
          >
            {display}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PanelDashboard — outer dispatcher
// ---------------------------------------------------------------------------

/**
 * Renders a `grammar === 'panel-dashboard'` sheet as an N-column CSS grid
 * of independently bordered key-value panels.
 *
 * The caller (<ModelGrid>) guarantees:
 *   - `layout.composition?.grammar === 'panel-dashboard'`
 *   - `layout.composition.panels?.length > 0`
 *
 * Returns `null` if the composition invariants aren't met (defensive; the
 * early-return in <ModelGrid> should prevent this path).
 */
// Maximum number of grid columns in the panel-dashboard layout (Lovable: 3).
// column indices > MAX_COLUMN are clamped to MAX_COLUMN (defensive).
const MAX_COLUMN = 2;

export function PanelDashboard({
  layout,
  cellValues,
  inputValues,
  schema,
  selectedCell,
  onCellSelect,
  zoom = DEFAULT_ZOOM,
  scrollToCell,
}: PanelDashboardProps) {
  // All hooks unconditional, BEFORE the early-return below (Rules of Hooks).
  // Pattern mirrors ModelGrid's: ref + state + effect declared at the top.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // Tree-navigator pulse target — `{row, col}` while pulsing, `null` after the
  // 2s timeout. Drives the `data-pulsing="true"` attribute on the matching
  // cell, used by the e2e walkthrough's viewport-intersection assertion and
  // matches the DataCell pulse contract from ModelGrid.tsx:320.
  const [pulsedCell, setPulsedCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  // Tree-navigator scroll-to-cell + pulse. Re-runs on every fresh
  // `scrollToCell` reference (parent bumps `nonce` per click — same contract
  // as ModelGrid.tsx:486-495). PanelDashboard is non-virtualized (CSS Grid),
  // so we use the native `Element.scrollIntoView` API against the matched
  // `[data-testid="panel-cell-${row}-${col}"]` node — the nearest scrollable
  // ancestor is the outer `[data-testid="panel-dashboard"]` container which
  // has `overflow-auto`. `block: 'center'` + `inline: 'center'` keeps the
  // target away from viewport edges (analog of virtualizer's
  // `align: 'center'`). Pulse follows the same 2s timeout as ModelGrid.
  useEffect(() => {
    if (!scrollToCell) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const target = container.querySelector(
      `[data-testid="panel-cell-${scrollToCell.row}-${scrollToCell.col}"]`
    );
    if (target instanceof HTMLElement) {
      target.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: 'smooth',
      });
    }
    setPulsedCell({ row: scrollToCell.row, col: scrollToCell.col });
    const timer = setTimeout(() => setPulsedCell(null), 2000);
    return () => clearTimeout(timer);
    // Re-fire on every fresh prop reference (nonce-bumped by parent).
  }, [scrollToCell]);

  // Both useMemo calls are unconditional (Rules of Hooks — must not be placed
  // after any conditional return). Same pattern as ModelGrid's useMemo.
  const cellIndex = useMemo(() => buildCellIndex(layout), [layout]);

  // Inc-4e: group panels by column index (clamped to MAX_COLUMN), sorted by
  // startRow ascending within each column. Computed unconditionally so the
  // hook order is stable — when composition is absent the result is an empty
  // Map and the early-return below fires before it is consumed.
  const sourcePanels = layout.composition?.panels;
  const columnMap = useMemo(() => {
    const map = new Map<number, CompositionPanel[]>();
    for (const panel of sourcePanels ?? []) {
      const col = Math.min(panel.column, MAX_COLUMN);
      if (!map.has(col)) map.set(col, []);
      map.get(col)!.push(panel);
    }
    // Sort panels within each column by startRow ascending so visual order
    // matches workbook row order.
    for (const colPanels of map.values()) {
      colPanels.sort((a, b) => a.startRow - b.startRow);
    }
    return map;
  }, [sourcePanels]);

  const composition = layout.composition;
  if (
    !composition ||
    composition.grammar !== 'panel-dashboard' ||
    !composition.panels?.length
  ) {
    return null;
  }

  // Derive column count from the clamped column map keys (max 3 tracks).
  const columnCount =
    columnMap.size > 0 ? Math.max(...columnMap.keys()) + 1 : 1;
  const colTemplate = gridTemplateColumns(columnCount, zoom / 100);

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 overflow-auto bg-bg p-3"
      data-testid="panel-dashboard"
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: colTemplate,
          gap: '12px',
          alignItems: 'start',
        }}
      >
        {Array.from(columnMap.entries()).map(([col, colPanels]) => (
          <div
            key={`panel-col-${col}`}
            data-testid={`panel-col-${col}`}
            style={{ gridColumn: col + 1 }}
            // flex flex-col gap-3: stack multiple panels within this grid track
            // vertically with 12px inter-panel spacing.
            // min-w-0: overrides the CSS Grid child default of min-width:auto
            // (= min-content), which causes the track to blow out to the
            // panel's intrinsic content width even when the track is declared
            // with 1fr. The outer container's `overflow-auto` handles the
            // horizontal-scroll case at higher zooms (post-ship remediation
            // 2026-05-25 — removed per-column `overflow-x-auto` to avoid
            // nested scrollbars and let labels keep their full width).
            className="flex flex-col gap-3 min-w-0"
          >
            {colPanels.map((panel) => {
              const cells = getCellsInPanel(cellIndex, panel);
              return (
                <PanelCard
                  key={`panel-${panel.column}-${panel.startRow}`}
                  panel={panel}
                  cells={cells}
                  cellValues={cellValues}
                  inputValues={inputValues}
                  schema={schema}
                  sheetName={layout.name}
                  selectedCell={selectedCell}
                  pulsedCell={pulsedCell}
                  onCellSelect={onCellSelect}
                  zoom={zoom}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
